import type { ReactNode } from "react";

/**
 * Generic visual badge for short status labels overlaid on cards,
 * selectors, and pricing rows ("OFERTA", "Premium", "Sin stock",
 * "Recomendado", "-15%"). Centralizes color tokens + sizing so a
 * change in brand voice does not require sweeping N files.
 *
 * Variants map to the project's semantic palette in `globals.css`:
 *
 *   - `primary`  — brand blue, neutral attention (default for nav cues).
 *   - `warning`  — yolk gold, gentle urgency (low-stock, recommended).
 *   - `success`  — green, positive state (in stock, applied coupon).
 *   - `danger`   — red, blocking error or hard out-of-stock.
 *   - `promo`    — yolk-on-dark, used for discount/OFERTA labels —
 *                  loud enough to read against vivid product photos.
 *   - `neutral`  — dark/85, used for generic info pills.
 */
export type BadgeVariant =
  | "primary"
  | "warning"
  | "success"
  | "danger"
  | "promo"
  | "neutral";

interface BadgeProps {
  variant?: BadgeVariant;
  children: ReactNode;
  /**
   * Optional leading icon (e.g. lucide `<Sparkles size={11} />`).
   * Sized 11px in the design system; the badge layout reserves the
   * gap regardless of icon presence.
   */
  icon?: ReactNode;
  /** Extra class names — for absolute positioning by callers. */
  className?: string;
  /**
   * When set, renders the badge with a slightly smaller padding —
   * suitable for inline use next to product titles.
   */
  size?: "sm" | "md";
}

const VARIANT_CLASSES: Record<BadgeVariant, string> = {
  primary: "bg-primary text-white",
  warning: "bg-yolk text-dark",
  success: "bg-success/15 text-success ring-1 ring-success/30",
  danger: "bg-alert text-white",
  // Promo: yolk gradient sits well on top of dark photos and matches
  // the existing Premium ribbon's visual weight without conflating
  // them — the icon side disambiguates (Tag vs Sparkles).
  promo: "bg-gradient-to-r from-yolk to-yolk-dark text-white shadow-md shadow-yolk-dark/30",
  neutral: "bg-dark/85 text-white",
};

export default function Badge({
  variant = "neutral",
  children,
  icon,
  className = "",
  size = "md",
}: BadgeProps) {
  const padding = size === "sm" ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-[11px]";
  return (
    <span
      className={`inline-flex items-center gap-1 ${padding} font-bold uppercase tracking-wider rounded-full ${VARIANT_CLASSES[variant]} ${className}`}
    >
      {icon}
      {children}
    </span>
  );
}
